import { IconBaseProps, IconType } from 'react-icons';
import { FaCodeBranch } from 'react-icons/fa';
import {
   HiMap,
   HiOfficeBuilding,
   HiOutlineMap,
   HiOutlineOfficeBuilding,
   HiRefresh,
   HiOutlineRefresh,
   HiOutlineGift,
   HiGift,
   HiOutlineStar,
   HiStar,
   HiDocumentRemove,
   HiOutlineDocumentRemove,
   HiOutlineCog,
   HiCog,
   HiCode,
   HiOutlineCode,
   HiOutlineClock,
   HiClock,
   HiOutlineExclamationCircle,
   HiExclamationCircle,
   HiOutlineCalendar,
   HiCalendar,
   HiOutlineCash,
   HiCash,
   HiOutlineScale,
   HiScale,
   HiOutlineShieldExclamation,
   HiShieldExclamation,
   HiOutlineHome,
   HiHome,
} from 'react-icons/hi';
import { TbTypography } from 'react-icons/tb';
import { HiCodeBracket, HiMapPin, HiOutlineMapPin, HiOutlineUsers, HiUsers } from 'react-icons/hi2';
import { AnyAbility } from '@casl/ability';
import { permissionActions, permissionKeys, routePaths } from 'src/utils/constants';

export interface VerticalNavType {
   activeIcon?: IconType;
   activeIconProps?: IconBaseProps;
   icon?: IconType;
   iconProps?: IconBaseProps;
   label: string;
   /** Every key is required (ProtectedRoute semantics); PLATFORM_ADMIN means manage-all. */
   permissions?: string[];
   route: string;
   /** Active only on the route itself, not on the routes nested under it. */
   exact?: boolean;
}

export const verticalNavConfigs: VerticalNavType[] = [
   {
      icon: HiOutlineOfficeBuilding,
      activeIcon: HiOfficeBuilding,
      label: 'sidebar.organizations',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.ORGANIZATION.INDEX,
   },
   {
      icon: HiOutlineMap,
      activeIcon: HiMap,
      label: 'sidebar.manageTrips',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.MANAGE_TRIP.INDEX,
   },
   {
      icon: HiOutlineCalendar,
      activeIcon: HiCalendar,
      label: 'sidebar.programSchedules',
      permissions: [permissionKeys.READ_SCHEDULE],
      route: routePaths.ADMIN.PROGRAM_SCHEDULE.INDEX,
   },
   {
      icon: HiOutlineOfficeBuilding,
      activeIcon: HiOfficeBuilding,
      label: 'sidebar.teamHotels',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.TEAM_HOTELS,
   },
   {
      icon: HiOutlineUsers,
      activeIcon: HiUsers,
      label: 'sidebar.users',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.USER.INDEX,
   },
   {
      icon: HiOutlineRefresh,
      activeIcon: HiRefresh,
      label: 'sidebar.transactions',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.TRANSACTIONS.INDEX,
   },
   {
      icon: HiOutlineCash,
      activeIcon: HiCash,
      label: 'sidebar.proceedsReport',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.PROCEEDS_REPORT.INDEX,
   },
   {
      icon: HiOutlineGift,
      activeIcon: HiGift,
      label: 'sidebar.coupon',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.COUPON.INDEX,
   },
   {
      icon: HiOutlineStar,
      activeIcon: HiStar,
      label: 'sidebar.review',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.REVIEW.INDEX,
   },
   {
      icon: HiOutlineDocumentRemove,
      activeIcon: HiDocumentRemove,
      label: 'sidebar.hotelBlacklist',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.HOTEL_BLACKLIST.INDEX,
   },
   {
      icon: FaCodeBranch,
      activeIcon: FaCodeBranch,
      label: 'sidebar.siteConfigRegistry',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.SITE_CONFIG_REGISTRY,
   },
   {
      icon: TbTypography,
      activeIcon: TbTypography,
      label: 'sidebar.fonts',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.FONTS,
   },
   {
      icon: HiOutlineClock,
      activeIcon: HiClock,
      label: 'sidebar.activityLogs',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.ACTIVITY_LOGS,
   },
   {
      icon: HiOutlineExclamationCircle,
      activeIcon: HiExclamationCircle,
      label: 'sidebar.bookingFailureTracker',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.BOOKING_FAILURE_TRACKER.INDEX,
   },
   {
      icon: HiOutlineScale,
      activeIcon: HiScale,
      label: 'sidebar.disputes',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.DISPUTES,
   },
   {
      icon: HiOutlineShieldExclamation,
      activeIcon: HiShieldExclamation,
      label: 'sidebar.fraudWatch',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.FRAUD_WATCH.INDEX,
   },
   {
      icon: HiOutlineMapPin,
      activeIcon: HiMapPin,
      label: 'sidebar.touristOverride',
      permissions: [permissionKeys.PLATFORM_ADMIN],
      route: routePaths.ADMIN.TOURIST_OVERRIDE,
   },
];

/**
 * NPT-2144 — what an org admin (not a platform admin) sees: their org area and read-only schedules.
 * Kept apart from the platform list so platform admins see exactly what they saw before.
 */
export const orgAdminNavConfigs: VerticalNavType[] = [
   {
      icon: HiOutlineHome,
      activeIcon: HiHome,
      label: 'sidebar.orgAdminHome',
      permissions: [permissionKeys.READ_ORG],
      route: routePaths.ADMIN.ORG_ADMIN.INDEX,
      exact: true,
   },
   {
      icon: HiOutlineCalendar,
      activeIcon: HiCalendar,
      label: 'sidebar.programSchedules',
      permissions: [permissionKeys.READ_SCHEDULE],
      route: routePaths.ADMIN.PROGRAM_SCHEDULE.INDEX,
   },
   {
      icon: HiOutlineMap,
      activeIcon: HiMap,
      label: 'sidebar.orgAdminTrips',
      permissions: [permissionKeys.READ_BOOKING],
      route: routePaths.ADMIN.ORG_ADMIN.TRIPS,
   },
   {
      icon: HiOutlineUsers,
      activeIcon: HiUsers,
      label: 'sidebar.users',
      permissions: [permissionKeys.READ_USER],
      route: routePaths.ADMIN.ORG_ADMIN.USERS,
   },
   {
      icon: HiOutlineCash,
      activeIcon: HiCash,
      label: 'sidebar.orgAdminProceeds',
      permissions: [permissionKeys.READ_BOOKING],
      route: routePaths.ADMIN.ORG_ADMIN.PROCEEDS,
   },
];

const allows = (ability: AnyAbility, permission: string) => {
   if (permission === permissionKeys.PLATFORM_ADMIN) return ability.can(permissionActions.MANAGE, 'all');
   const [action, subject] = permission.split('_');
   return ability.can(action.toLowerCase(), subject.toLowerCase());
};

export const canSeeNavItem = (ability: AnyAbility, item: VerticalNavType) => (item.permissions ?? []).every(permission => allows(ability, permission));

/** The sidebar for this ability: the platform list for platform admins, the org admin list otherwise. */
export const getVisibleNavItems = (ability: AnyAbility): VerticalNavType[] => {
   const source = ability.can(permissionActions.MANAGE, 'all') ? verticalNavConfigs : orgAdminNavConfigs;
   return source.filter(item => canSeeNavItem(ability, item));
};
